import React, { useState, useEffect, useRef } from 'react';
import { secureLogger } from '../utils/secureLogger';
import { sanitizeUserInput, sanitizeBase64Image } from '../utils/sanitizer';

interface MapComponentProps {
  gpsData: any[];
  photoData?: any[];
  currentLocation?: { latitude: number; longitude: number };
}

const createInfoWindowContent = (
  title: string,
  titleColor: string,
  rows: Array<{ label: string; value: string }>,
) => {
  const container = document.createElement('div');
  container.style.padding = '8px';
  container.style.minWidth = '200px';

  const heading = document.createElement('h4');
  heading.style.margin = '0 0 8px 0';
  heading.style.color = titleColor;
  heading.textContent = title;
  container.appendChild(heading);

  rows.forEach(({ label, value }) => {
    const paragraph = document.createElement('p');
    paragraph.style.margin = '4px 0';

    const strong = document.createElement('strong');
    strong.textContent = `${label}:`;
    paragraph.appendChild(strong);
    paragraph.append(` ${value}`);
    container.appendChild(paragraph);
  });

  return container;
};

const MapComponent: React.FC<MapComponentProps> = ({ gpsData, photoData = [], currentLocation }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const pathRef = useRef<any>(null);
  const directionsServiceRef = useRef<any>(null);
  const directionsRendererRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGoogleMapsAPI();
  }, []);

  const loadGoogleMapsAPI = () => {
    // 既に読み込まれている場合
    if (window.google && window.google.maps && window.google.maps.MapTypeId) {
      setIsLoading(false);
      setTimeout(() => initializeMap(), 100); // 少し遅延させて確実に初期化
      return;
    }

    // 既にスクリプトが存在するか確認
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // 既存スクリプトの読み込み完了を待つ（タイムアウト付き）
      let attempts = 0;
      const maxAttempts = 50; // 5秒でタイムアウト
      const checkGoogleMaps = setInterval(() => {
        attempts++;
        if (window.google && window.google.maps && window.google.maps.MapTypeId) {
          clearInterval(checkGoogleMaps);
          setIsLoading(false);
          setTimeout(() => initializeMap(), 100);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkGoogleMaps);
          setError('Google Maps APIの読み込みがタイムアウトしました');
          setIsLoading(false);
        }
      }, 100);
      return;
    }

    // APIキーを取得
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY_HERE') {
      setError('Google Maps APIキーが設定されていません');
      setIsLoading(false);
      return;
    }

    // スクリプトを非同期で動的に読み込み
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry,marker&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Google Maps APIの完全な読み込みを待つ（タイムアウト付き）
      let attempts = 0;
      const maxAttempts = 50;
      const checkGoogleMaps = setInterval(() => {
        attempts++;
        if (window.google && window.google.maps && window.google.maps.MapTypeId) {
          clearInterval(checkGoogleMaps);
          setIsLoading(false);
          setTimeout(() => initializeMap(), 200); // さらに遅延させて確実に初期化
        } else if (attempts >= maxAttempts) {
          clearInterval(checkGoogleMaps);
          setError('Google Maps APIの初期化がタイムアウトしました');
          setIsLoading(false);
        }
      }, 100);
    };
    script.onerror = () => {
      setError('Google Maps APIの読み込みに失敗しました');
      setIsLoading(false);
    };
    document.head.appendChild(script);
  };

  const initializeMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps || !window.google.maps.MapTypeId) {
      secureLogger.warn('Google Maps API not fully loaded yet');
      return;
    }

    // 初期位置（東京駅）
    const defaultCenter = { lat: 35.681236, lng: 139.767125 };
    const center = currentLocation 
      ? { lat: currentLocation.latitude, lng: currentLocation.longitude }
      : defaultCenter;

    // マップ初期化（Map IDを追加してAdvancedMarkerを有効化）
    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 16,
      center: center,
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      mapId: 'THINKLET_MAP_ID', // AdvancedMarkerElementのためのMap ID
    });

    // Directions Service初期化
    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: '#1890ff',
        strokeWeight: 4,
        strokeOpacity: 0.8
      }
    });
    directionsRenderer.setMap(map);

    mapInstanceRef.current = map;
    directionsServiceRef.current = directionsService;
    directionsRendererRef.current = directionsRenderer;

    secureLogger.info('Map initialized');
  };

  useEffect(() => {
    if (!mapInstanceRef.current || !directionsServiceRef.current) return;

    // 既存のマーカーをクリア
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // データがない場合は早期リターン
    if (!gpsData.length && !photoData.length) return;

    // GPSデータがある場合のみピンを立てる
    if (gpsData && gpsData.length > 0) {
      gpsData.forEach((point, index) => {
        if (!point || typeof point.latitude !== 'number' || typeof point.longitude !== 'number') {
          secureLogger.warn('Invalid GPS point data', { index, point });
          return;
        }
      const isLatest = index === gpsData.length - 1;
      const isFirst = index === 0;
      
      // ピンの色を決定
      let pinColor = '#1890ff'; // デフォルト（青）
      if (isFirst) pinColor = '#52c41a'; // 開始点（緑）
      if (isLatest) pinColor = '#f5222d'; // 現在位置（赤）
      
      // 番号付きピンのSVGアイコンを作成
      const pinIcon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 0C6.7 0 0 6.7 0 15c0 8.3 15 25 15 25s15-16.7 15-25C30 6.7 23.3 0 15 0z" fill="${pinColor}" stroke="#fff" stroke-width="2"/>
            <circle cx="15" cy="15" r="10" fill="#fff"/>
            <text x="15" y="20" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" font-weight="bold" fill="${pinColor}">${index + 1}</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(30, 40),
        anchor: new window.google.maps.Point(15, 40)
      };
      
      const marker = new window.google.maps.Marker({
        position: { lat: point.latitude, lng: point.longitude },
        map: mapInstanceRef.current,
        title: `位置 ${index + 1}: ${sanitizeUserInput(point.location_name) || '不明'} (${new Date((point.measured_at || point.timestamp).replace(/Z$/, '')).toLocaleTimeString()})`,
        icon: pinIcon,
        zIndex: isLatest ? 1000 : (isFirst ? 999 : 100 + index)
      });
      
      // 情報ウィンドウを追加
      const rows = [
        { label: '場所', value: sanitizeUserInput(point.location_name || '不明') },
        { label: '座標', value: `${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}` },
      ];
      if (point.altitude !== undefined) {
        rows.push({ label: '高度', value: `${point.altitude.toFixed(1)}m` });
      }
      if (point.accuracy !== undefined) {
        rows.push({ label: '精度', value: `${point.accuracy.toFixed(1)}m` });
      }
      rows.push({
        label: '時刻',
        value: new Date((point.measured_at || point.timestamp).replace(/Z$/, '')).toLocaleString(),
      });
      if (point.direction !== undefined) {
        rows.push({ label: '方向', value: `${Math.round(point.direction)}°` });
      }

      const infoWindow = new window.google.maps.InfoWindow({
        content: createInfoWindowContent(`位置 ${index + 1}`, pinColor, rows)
      });
      
      marker.addListener('click', () => {
        // 他の情報ウィンドウを閉じる
        markersRef.current.forEach(m => {
          if (m.infoWindow) m.infoWindow.close();
        });
        infoWindow.open(mapInstanceRef.current, marker);
      });
      
      // 情報ウィンドウをマーカーに関連付け
      (marker as any).infoWindow = infoWindow;
        markersRef.current.push(marker);
      });
    }

    // GPSポイントを直線で結ぶ
    if (gpsData.length >= 2) {
      secureLogger.debug('Showing path for GPS points', { count: gpsData.length });
      if (pathRef.current) pathRef.current.setMap(null);
      
      const path = gpsData.map(item => ({
        lat: item.latitude,
        lng: item.longitude
      }));

      const polyline = new window.google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: '#1890ff',
        strokeOpacity: 0.8,
        strokeWeight: 3
      });
      polyline.setMap(mapInstanceRef.current);
      pathRef.current = polyline;
    }

    // 写真サムネイルを直接表示
    secureLogger.debug('Processing photo data', { count: photoData.length });
    photoData.forEach((photo, index) => {
      // 位置情報がない写真はスキップ
      if (!photo.gps_latitude || !photo.gps_longitude) {
        secureLogger.debug('Skipping photo - no location data');
        return;
      }
      
      secureLogger.debug('Creating thumbnail marker');
      
      // 写真データのフィールド名を確認
      const imageData = photo.image_data || photo.imageData || photo.data;
      if (!imageData) {
        secureLogger.error('No image data for photo');
        return;
      }
      secureLogger.debug('Image data loaded', { length: imageData.length });
      
      // サムネイルアイコンを作成（円形にクロップ）
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = 60;
      canvas.width = size;
      canvas.height = size;
      
      const img = new Image();
      img.onload = () => {
        // 画像を描画（四角形）
        ctx!.drawImage(img, 0, 0, size, size);
        
        // 白い枠線を追加（四角形）
        ctx!.strokeStyle = '#fff';
        ctx!.lineWidth = 4;
        ctx!.strokeRect(2, 2, size - 4, size - 4);
        
        // CanvasをData URLに変換
        const thumbnailDataUrl = canvas.toDataURL('image/png');
        
        // マーカーを作成
        const thumbnailIcon = {
          url: thumbnailDataUrl,
          scaledSize: new window.google.maps.Size(size, size),
          anchor: new window.google.maps.Point(size/2, size/2)
        };
        
        const photoMarker = new window.google.maps.Marker({
          position: { lat: photo.gps_latitude, lng: photo.gps_longitude },
          map: mapInstanceRef.current,
          title: `写真 ${index + 1}: ${new Date(photo.captured_at).toLocaleString()}`,
          icon: thumbnailIcon,
          zIndex: 2000 + index
        });
        
        // 情報ウィンドウを追加
        const photoInfoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; min-width: 300px; max-width: 400px;">
              <h4 style="margin: 0 0 8px 0; color: #ff4d4f;">写真 ${index + 1}</h4>
              <img src="data:image/jpeg;base64,${sanitizeBase64Image(imageData)}" 
                   style="width: 100%; max-width: 350px; height: auto; border-radius: 4px; margin-bottom: 8px; cursor: pointer;" 
                   onclick="window.open('data:image/jpeg;base64,${sanitizeBase64Image(imageData)}', '_blank')" 
                   title="クリックで拡大表示" />
              ${photo.gps_latitude && photo.gps_longitude ? `<p style="margin: 4px 0;"><strong>座標:</strong> ${photo.gps_latitude.toFixed(6)}, ${photo.gps_longitude.toFixed(6)}</p>` : ''}
              <p style="margin: 4px 0;"><strong>時刻:</strong> ${new Date(photo.captured_at).toLocaleString()}</p>
              ${photo.imu_orientation ? `<p style="margin: 4px 0;"><strong>方向:</strong> ${Math.round(photo.imu_orientation)}°</p>` : ''}
            </div>
          `
        });
        
        photoMarker.addListener('click', () => {
          markersRef.current.forEach(m => {
            if (m.infoWindow) m.infoWindow.close();
          });
          photoInfoWindow.open(mapInstanceRef.current, photoMarker);
        });
        
        (photoMarker as any).infoWindow = photoInfoWindow;
        markersRef.current.push(photoMarker);
        secureLogger.debug('Created thumbnail marker');
      };
      
      img.onerror = () => {
        secureLogger.error('Failed to load image for photo');
        // フォールバック: カメラアイコンを使用
        const cameraIcon = {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="14" fill="#ff4d4f" stroke="#fff" stroke-width="3"/>
              <rect x="8" y="11" width="16" height="10" rx="2" fill="#fff"/>
              <circle cx="16" cy="16" r="3" fill="#ff4d4f"/>
              <text x="16" y="26" text-anchor="middle" font-family="Arial" font-size="8" font-weight="bold" fill="#fff">${index + 1}</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 16)
        };
        
        const photoMarker = new window.google.maps.Marker({
          position: { lat: photo.gps_latitude, lng: photo.gps_longitude },
          map: mapInstanceRef.current,
          title: `写真 ${index + 1}: ${new Date(photo.captured_at).toLocaleString()}`,
          icon: cameraIcon,
          zIndex: 2000 + index
        });
        
        markersRef.current.push(photoMarker);
      };
      
      // Base64データを読み込み
      img.src = `data:image/jpeg;base64,${imageData}`;
    });

    // 最新位置に地図を移動（GPSデータがある場合）
    if (gpsData.length > 0) {
      const latest = gpsData[gpsData.length - 1];
      mapInstanceRef.current.setCenter({ lat: latest.latitude, lng: latest.longitude });
    } else if (photoData.length > 0) {
      // GPSデータがない場合は最新の写真位置に移動
      const latest = photoData[photoData.length - 1];
      mapInstanceRef.current.setCenter({ lat: latest.gps_latitude, lng: latest.gps_longitude });
    }
  }, [gpsData, photoData]);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        minHeight: '500px'
      }}>
        <div>マップを読み込み中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        minHeight: '500px',
        color: '#ff4d4f'
      }}>
        <div>
          <div>{error}</div>
          <div style={{ fontSize: '12px', marginTop: '8px' }}>
            .envファイルにREACT_APP_GOOGLE_MAPS_API_KEYを設定してください
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={mapRef} 
      style={{ 
        width: '100%', 
        height: '100%',
        minHeight: '500px',
        borderRadius: '6px'
      }} 
    />
  );
};

export default MapComponent;
